import React, { useEffect, useState } from 'react';

import { Modal } from '@/components/Modal';
import type { Agent } from '@/hooks/useAgents';

import { useActiveDirectoryCredentials } from './useActiveDirectoryCredentials';

/**
 * Props for the legacy interface (used by CredentialsManagementTab)
 */
interface LegacyAddCredentialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    label: string;
    username: string;
    password: string;
    domain: string;
  }) => void;
  isSubmitting: boolean;
  agent?: never;
  onSuccess?: never;
}

/**
 * Props for the self-contained interface (used by row actions)
 * Following IA principles: direct modal access from row actions
 */
interface SelfContainedAddCredentialModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  onSuccess?: () => void;
  onSubmit?: never;
  isSubmitting?: never;
}

type AddCredentialModalProps =
  | LegacyAddCredentialModalProps
  | SelfContainedAddCredentialModalProps;

/**
 * Modal for adding Active Directory credentials to an Aegis agent.
 *
 * Supports two modes:
 * 1. Legacy mode: Receives onSubmit callback (used by CredentialsManagementTab)
 * 2. Self-contained mode: Receives agent and handles submission internally (used by row actions)
 *
 * Following IA principles: enables direct modal access from row actions
 * without requiring drawer navigation.
 */
export const AddCredentialModal: React.FC<AddCredentialModalProps> = props => {
  const { isOpen, onClose } = props;

  // Determine if we're in self-contained mode (has agent prop)
  const isSelfContained = 'agent' in props && props.agent !== undefined;

  // For self-contained mode, use the hook internally
  // This is safe because we're not conditionally calling the hook
  const credentials = useActiveDirectoryCredentials(
    isSelfContained ? props.agent : ({ client_id: '' } as Agent)
  );

  const [formData, setFormData] = useState({
    label: '',
    username: '',
    password: '',
    domain: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ label: '', username: '', password: '', domain: '' });
      setErrors({});
    }
  }, [isOpen]);

  const handleClose = () => {
    setFormData({ label: '', username: '', password: '', domain: '' });
    setErrors({});
    onClose();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (!formData.domain.includes('.')) {
      newErrors.domain = 'Domain must be in FQDN format (e.g., corp.local)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (isSelfContained) {
      // Self-contained mode: handle submission internally
      setIsSubmitting(true);
      try {
        await credentials.addCredential.mutateAsync({
          ...formData,
          resourceKey: props.agent.client_id,
        });
        handleClose();
        props.onSuccess?.();
      } catch {
        // Error handled by mutation's onError
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Legacy mode: call the provided onSubmit
      props.onSubmit(formData);
    }
  };

  // Determine submitting state based on mode
  const submitting = isSelfContained ? isSubmitting : props.isSubmitting;

  // Check if all required fields are filled (matching AddUserModal pattern)
  const isFormValid =
    formData.label.trim() !== '' &&
    formData.username.trim() !== '' &&
    formData.password.trim() !== '' &&
    formData.domain.trim() !== '';

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Add Active Directory Credential"
      footer={{
        text: submitting ? 'Adding...' : 'Add Credential',
        onClick: handleSubmit,
        disabled: submitting || !isFormValid,
      }}
      data-testid="add-credential-modal"
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">
            Label <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.label}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ ...formData, label: e.target.value })
            }
            placeholder="e.g., Domain Admin"
            className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-brand w-full rounded-md border p-2 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="credential-label-input"
            disabled={submitting}
          />
          {errors.label && (
            <p className="mt-1 text-sm text-red-500">{errors.label}</p>
          )}
          <p className="text-chariot-text-muted mt-1 text-sm">
            A friendly name to identify this credential
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            Domain <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.domain}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ ...formData, domain: e.target.value })
            }
            placeholder="e.g., corp.local"
            className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-brand w-full rounded-md border p-2 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="credential-domain-input"
            disabled={submitting}
          />
          {errors.domain && (
            <p className="mt-1 text-sm text-red-500">{errors.domain}</p>
          )}
          <p className="text-chariot-text-muted mt-1 text-sm">
            Fully qualified domain name (FQDN)
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            Username <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ ...formData, username: e.target.value })
            }
            placeholder="e.g., administrator@corp.local"
            className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-brand w-full rounded-md border p-2 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="credential-username-input"
            disabled={submitting}
          />
          {errors.username && (
            <p className="mt-1 text-sm text-red-500">{errors.username}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            Password <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ ...formData, password: e.target.value })
            }
            placeholder="Enter password"
            className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:ring-brand w-full rounded-md border p-2 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="credential-password-input"
            disabled={submitting}
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-500">{errors.password}</p>
          )}
        </div>
      </div>
    </Modal>
  );
};
